import { useState, useEffect, useRef } from 'react';
import { useUi } from '../i18n/uiTranslations';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../i18n/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ChevronDown, ArrowUpRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToIdWhenReady, scrollToId } from '../lib/gsap';
import { useSceneNav } from './SceneTransition';

const LOGO = 'https://customer-assets.emergentagent.com/job_pay-gaming/artifacts/9msxlx6e_logo_Logo%20Text%20-%20White.png';

export const SERVICE_LINKS = [
  { key: 'aggregator', link: '/services/aggregator' },
  { key: 'platform', link: '/services/platform' },
  { key: 'payments', link: '/services/payments' },
  { key: 'crypto', link: '/services/crypto' },
];

const ServicesDropdown = ({ t, onNavigate, onViewAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={(e) => setOpen((o) => (e.detail === 0 ? !o : true))}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1.5 text-[0.9rem] transition-colors duration-300 ${open ? 'text-[#E8D6AF]' : 'text-[#F4F0E8]/75 hover:text-[#F4F0E8]'}`}
        data-testid="nav-services-dropdown"
      >
        {t('nav.services')}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[440px]"
            data-testid="nav-services-panel"
          >
            <div className="rounded-2xl bg-[#101010]/95 backdrop-blur-xl border border-[#F4F0E8]/10 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
              {SERVICE_LINKS.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => { setOpen(false); onNavigate(s.link); }}
                  className="group w-full flex items-start gap-4 text-left p-4 rounded-xl hover:bg-[#F4F0E8]/[0.04] focus-visible:bg-[#F4F0E8]/[0.04] transition-colors"
                  data-testid={`nav-service-${s.key}`}
                >
                  <span className="font-mono-lux text-[0.7rem] text-[#C8AA73] pt-1">0{i + 1}</span>
                  <span className="flex-1">
                    <span className="block font-display text-lg text-[#F4F0E8] group-hover:text-[#E8D6AF] transition-colors">{t(`services.${s.key}.title`)}</span>
                    <span className="block text-[0.8rem] text-[#ABA59A] leading-snug mt-1 line-clamp-2">{t(`services.${s.key}.desc`)}</span>
                  </span>
                  <ArrowUpRight className="w-4 h-4 mt-1 text-[#7C776E] group-hover:text-[#C8AA73] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
                </button>
              ))}
              <div className="border-t border-[#F4F0E8]/[0.07] mt-1 px-4 py-3">
                <button onClick={() => { setOpen(false); onViewAll(); }} className="link-lux text-[0.8rem] text-[#C8AA73]" data-testid="nav-view-all-services">
                  {t('nav.viewAllServices')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LanguageSwitcher = ({ language, setLanguage }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button
        className="flex items-center gap-1.5 font-mono-lux text-[0.75rem] tracking-[0.12em] text-[#F4F0E8]/75 hover:text-[#F4F0E8] px-2 min-h-[44px] rounded-md transition-colors"
        data-testid="lang-switcher"
        aria-label="Language"
      >
        {language.toUpperCase()}
        <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={12} className="bg-[#101010]/95 backdrop-blur-xl border-[#F4F0E8]/10 min-w-[190px] rounded-xl p-1.5">
      {languages.map((lang) => (
        <DropdownMenuItem
          key={lang.code}
          onClick={() => setLanguage(lang.code)}
          className={`cursor-pointer rounded-lg px-3 py-2.5 focus:bg-[#F4F0E8]/[0.05] ${language === lang.code ? 'text-[#E8D6AF]' : 'text-[#F4F0E8]/75'}`}
          data-testid={`lang-${lang.code}`}
        >
          <span className="font-mono-lux text-[0.7rem] w-7 text-[#C8AA73]">{lang.code.toUpperCase()}</span>
          <span className="flex-1 text-sm">{lang.name}</span>
          {language === lang.code && <Check className="w-3.5 h-3.5 text-[#C8AA73]" strokeWidth={1.5} />}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileMenu = ({ t, language, setLanguage, onNavigate, onSection, onDemo }) => {
  const u = useUi();
  const items = [
    ...SERVICE_LINKS.map((s) => ({ id: `mobile-service-${s.key}`, label: t(`services.${s.key}.title`), action: () => onNavigate(s.link), small: true })),
    { id: 'mobile-nav-about', label: t('nav.about'), action: () => onSection('about') },
    { id: 'mobile-nav-contact', label: t('nav.contact'), action: () => onSection('contact') },
  ];
  return (
    <motion.div
      className="lg:hidden fixed inset-0 bg-[#080808] z-40 overflow-y-auto overscroll-contain"
      style={{ top: 'calc(72px + env(safe-area-inset-top))' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="mobile-menu"
    >
      <nav className="px-6 pt-8 flex flex-col min-h-full" style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' }}>
        <span className="eyebrow mb-4">{t('nav.services')}</span>
        {items.map((it, i) => (
          <motion.button
            key={it.id}
            onClick={it.action}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 + i * 0.045, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`text-left font-display text-[#F4F0E8] border-b border-[#F4F0E8]/[0.07] py-4 hover:text-[#E8D6AF] transition-colors ${it.small ? 'text-2xl' : 'text-3xl'} ${i === 4 ? 'mt-6' : ''}`}
            data-testid={it.id}
          >
            {it.label}
          </motion.button>
        ))}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="mobile-lang-list">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => setLanguage(l.code)}
              className={`font-mono-lux text-xs min-w-[44px] min-h-[44px] px-3 rounded-full border transition-colors ${language === l.code ? 'border-[#C8AA73] text-[#E8D6AF]' : 'border-[#F4F0E8]/15 text-[#ABA59A]'}`}
              data-testid={`mobile-lang-${l.code}`}
            >
              {l.code.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={onDemo} className="btn-primary w-full mt-10" data-testid="mobile-demo-button">{t('nav.demo')}</button>
      </nav>
    </motion.div>
  );
};

export const Header = ({ onDemoClick }) => {
  const { language, setLanguage, t } = useLanguage();
  const u = useUi();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const sceneGo = useSceneNav();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      scrollToIdWhenReady(id);
    } else {
      setTimeout(() => scrollToId(id), 60);
    }
  };

  const go = (link) => {
    setMobileMenuOpen(false);
    if (link.startsWith('/services/')) sceneGo(link, document.activeElement !== document.body ? document.activeElement : null);
    else navigate(link);
  };

  const handleDemo = () => {
    setMobileMenuOpen(false);
    if (onDemoClick) onDemoClick();
    else navigate('/?demo=true');
  };

  const goHome = () => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };

  const navBtn = 'text-[0.9rem] text-[#F4F0E8]/75 hover:text-[#F4F0E8] transition-colors duration-300';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled || mobileMenuOpen ? 'glass' : 'bg-transparent border-b border-transparent'}`}
      data-testid="header"
    >
      <div className="max-w-[1440px] mx-auto pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))] lg:px-12" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="flex items-center justify-between h-[72px] lg:h-[84px]">
          <button onClick={goHome} className="flex items-center shrink-0" data-testid="logo" aria-label="SOFTMAYA">
            <img src={LOGO} alt="SOFTMAYA" className="h-7 min-[360px]:h-9 lg:h-11 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
            <ServicesDropdown t={t} onNavigate={go} onViewAll={() => scrollToSection('services')} />
            <button onClick={() => scrollToSection('about')} className={navBtn} data-testid="nav-about">{t('nav.about')}</button>
            <button onClick={() => scrollToSection('contact')} className={navBtn} data-testid="nav-contact">{t('nav.contact')}</button>
          </nav>

          <div className="flex items-center gap-3 lg:gap-5">
            <LanguageSwitcher language={language} setLanguage={setLanguage} />
            <span className="hidden md:block w-px h-5 bg-[#F4F0E8]/15" />
            <button onClick={handleDemo} className="hidden md:inline-flex btn-primary !py-2.5 !px-5 !text-[0.8rem] min-h-[44px]" data-testid="hero-demo-button">
              {t('nav.demo')}
            </button>
            <button
              className="lg:hidden relative w-11 h-11 flex flex-col items-center justify-center gap-[6px]"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-button"
            >
              <span className={`block h-px w-6 bg-[#F4F0E8] transition-transform duration-500 ${mobileMenuOpen ? 'translate-y-[3.5px] rotate-45' : ''}`} />
              <span className={`block h-px w-6 bg-[#F4F0E8] transition-transform duration-500 ${mobileMenuOpen ? '-translate-y-[3.5px] -rotate-45' : ''}`} />
            </button>
          </div>
        </div>
      </div>
      {createPortal(
        <AnimatePresence>
          {mobileMenuOpen && (
            <MobileMenu t={t} language={language} setLanguage={setLanguage} onNavigate={go} onSection={scrollToSection} onDemo={handleDemo} />
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};
